"use client";

import { useState } from "react";
import { Send } from "lucide-react";

interface InviteFormProps {
  sessionId: string;
  resourcePath?: string;
}

export default function InviteForm({ sessionId, resourcePath }: InviteFormProps) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<"view" | "edit">("view");
  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email) {
      setErrorMessage("Please enter an email address");
      return;
    }

    setIsSubmitting(true);
    setSuccessMessage("");
    setErrorMessage("");

    try {
      const response = await fetch(`/api/sessions/${sessionId}/invite`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, role, message, resourcePath }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to send invite");
      }

      setSuccessMessage(`Invite sent to ${email}!`);
      setEmail("");
      setMessage("");

      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : "Failed to send invite"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="stack gap-4">
      {/* Email Input */}
      <div className="stack gap-2">
        <label htmlFor="email" className="text-sm font-bold uppercase">
          Email Address
        </label>
        <input
          type="email"
          id="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="colleague@example.com"
          className="border-4 border-black px-4 py-3 text-base"
          disabled={isSubmitting}
        />
      </div>

      {/* Role Selector */}
      <div className="stack gap-2">
        <label className="text-sm font-bold uppercase">Access Level</label>
        <div className="flex gap-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="role"
              value="view"
              checked={role === "view"}
              onChange={(e) => setRole(e.target.value as "view")}
              className="w-5 h-5"
              disabled={isSubmitting}
            />
            <div className="stack gap-1">
              <span className="font-bold">View Only</span>
              <span className="text-xs text-gray-600">
                Can see ideas and progress, but can't make changes
              </span>
            </div>
          </label>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="role"
              value="edit"
              checked={role === "edit"}
              onChange={(e) => setRole(e.target.value as "edit")}
              className="w-5 h-5"
              disabled={isSubmitting}
            />
            <div className="stack gap-1">
              <span className="font-bold">Can Edit</span>
              <span className="text-xs text-gray-600">
                Can add ideas, complete bets, and modify the session
              </span>
            </div>
          </label>
        </div>
      </div>

      {/* Optional Message */}
      <div className="stack gap-2">
        <label htmlFor="message" className="text-sm font-bold uppercase">
          Personal Message (Optional)
        </label>
        <textarea
          id="message"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Hey! I'd love for you to check out this session..."
          rows={3}
          className="border-4 border-black px-4 py-3 text-base resize-none"
          disabled={isSubmitting}
        />
      </div>

      {/* Success/Error Messages */}
      {successMessage && (
        <div className="border-4 border-green-500 bg-green-50 px-4 py-3 text-green-900 font-bold">
          {successMessage}
        </div>
      )}

      {errorMessage && (
        <div className="border-4 border-red-500 bg-red-50 px-4 py-3 text-red-900 font-bold">
          {errorMessage}
        </div>
      )}

      {/* Submit Button */}
      <button
        type="submit"
        disabled={isSubmitting}
        className="w-full border-4 border-black bg-purple text-white font-black uppercase px-6 py-4 hover:bg-pink transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
      >
        <Send className="w-5 h-5" />
        {isSubmitting ? "Sending..." : "Send Invite"}
      </button>
    </form>
  );
}
